import { ButtonTab, ButtonTabs, useHasPermission, useIsAddonEnabled } from "@dgs/core";
import { isBefore } from "date-fns";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";
import styled from "styled-components";
import { shouldSkipOffboarding } from "../../../../shared/events/offboarding/eventOffboardingStepperUtils";
import { LinkToOffboardingButton } from "../../../../shared/events/offboarding/LinkToOffboardingButton";
import { shouldSkipPlannedCostStep } from "../../../../shared/events/save/eventStepperUtils";
import { WarningBanner } from "../../../../shared/ui/WarningBanner";
import { EventDetailResource } from "../../shared/types";
import { EventCostsCard } from "./EventCostsCard";
import { EventKeyMetricsCard } from "./EventKeyMetricsCard";
import { EventOverviewCard } from "./EventOverviewCard";
import { EventParticipantsCard } from "./EventParticipantsCard";

const EventDetailsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(5)};

	${({ theme }) => theme.breakpoints.desktop} {
		grid-template-columns: 400px 1fr;
	}
`;

const EventInfoWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(5)};
`;

const OffboardingWarning = styled(WarningBanner)`
	margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

interface Props {
	event: EventDetailResource;
}

export const EventOverview: FC<Props> = ({ event }) => {
	const { t } = useTranslation();
	const isPressModuleEnabled = useIsAddonEnabled("pressModule");
	const isContactDatabaseEnabled = useIsAddonEnabled("contactDatabase");
	const canReadContacts = useHasPermission(["contact.read"]);
	const canReadClippingProviderContacts = useHasPermission(["clippingProviderContact.read"]);
	const hasParticipantManagement = event && event.applications.some(({ type }) => type === "PARTICIPANT_MANAGEMENT");
	const canReadCostCategories = useHasPermission(["costCategory.read"]) || event.hasProjectMemberPermissions;
	const canReadFeedback = useHasPermission(["event.update"]) || event.hasProjectManagerPermissions;
	const hasEventEnded = isBefore(event.end, new Date());

	return (
		<>
			{hasEventEnded &&
				!shouldSkipOffboarding(event.eventType) &&
				!event.hasFinishedOffboarding &&
				canReadCostCategories && (
					<OffboardingWarning
						hasIcon={false}
						warning={t("The event is over, you can now start the offboarding process.")}
						actions={<LinkToOffboardingButton eventType={event.eventType} />}
					/>
				)}
			<EventDetailsGrid>
				<EventInfoWrapper>
					<EventOverviewCard event={event} isArchived={false} />
					{event.eventType.plannedPersonCountRequirement !== "prohibited" && isContactDatabaseEnabled && (
						<EventParticipantsCard event={event} />
					)}
					{!shouldSkipPlannedCostStep(event.eventType) && isContactDatabaseEnabled && canReadCostCategories && (
						<EventCostsCard event={event} />
					)}
					{canReadFeedback && event.hasFinishedOffboarding && <EventKeyMetricsCard event={event} />}
				</EventInfoWrapper>
				<EventInfoWrapper>
					<ButtonTabs>
						<ButtonTab to="../details">{t("Details")}</ButtonTab>
						<ButtonTab to="../users">{t("Users")}</ButtonTab>
						{hasParticipantManagement && isContactDatabaseEnabled && canReadContacts && (
							<ButtonTab to="../contacts">{t("Contacts")}</ButtonTab>
						)}
						{isContactDatabaseEnabled && canReadContacts && (
							<ButtonTab to="../invitation-list">{t("Invitation list")}</ButtonTab>
						)}
						{hasParticipantManagement &&
							isContactDatabaseEnabled &&
							isPressModuleEnabled &&
							canReadClippingProviderContacts && (
								<ButtonTab to="../clipping-provider-contacts">{`${t("Contacts")} (${t("Clipping provider")})`}</ButtonTab>
							)}
						{!shouldSkipPlannedCostStep(event.eventType) && isContactDatabaseEnabled && canReadCostCategories && (
							<ButtonTab to="../cost-categories">{t("Cost categories")}</ButtonTab>
						)}
						{canReadFeedback && <ButtonTab to="../feedback">{t("Feedback")}</ButtonTab>}
					</ButtonTabs>
					<Outlet />
				</EventInfoWrapper>
			</EventDetailsGrid>
		</>
	);
};
